import * as React from 'react';
import { TYPOGRAPHY_SIZE, TypographySize } from 'mm-theme-configuration/dist/consts';
import { css, StyleSheet } from 'aphrodite/no-important';
import { useTheme } from '../../theming/useTheme';
import { VrLineHeight } from '../../theming/verticalRhythm/verticalRhythm';
import { MEDIA_BREAKPOINTS } from '../../mediaQueries.const';
import { ErrorBoundary } from '../errorHandling/ErrorBoundary';
import { QuoteIcon } from './QuoteIcon';
import { MandatoryAdditionalStyle } from '../components.utils';
import { H3 } from '../../typography/semanticTags';
import { FontSizesStyles, semanticCSS } from '../../typography/semanticTags/semanticTags.utils';

const componentConfigurationKey = 'quote';

interface QuoteComponentDataProps {
  text: string
  cite: string
  dataId?: string
}

interface QuoteComponentThemeProps {
  vrLineHeight: VrLineHeight
  borderColor: string
  textColor: string
  citeColor: string
  iconColor: string
  fontSizesStyles: FontSizesStyles
}

type QuoteComponentProps = QuoteComponentDataProps & QuoteComponentThemeProps & MandatoryAdditionalStyle;

const getStyles = (theme: QuoteComponentThemeProps) => {
  const { vrLineHeight, borderColor, textColor, citeColor } = theme;
  return StyleSheet.create({
    wrapper: {
      position: 'relative',
      borderBottom: `1px solid ${borderColor}`,
      [MEDIA_BREAKPOINTS.LARGE]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE]}px 0 0 0`,
        paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM]}px 0 0 0`,
        paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        margin: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL]}px 0 0 0`,
        paddingBottom: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
      },
    },
    text: {
      fontStyle: 'italic',
      color: textColor,
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.5}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.5}px`,
      },
    },
    cite: {
      color: citeColor,
      fontStyle: 'normal',
      [MEDIA_BREAKPOINTS.LARGE]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.LARGE] * 0.25}px`,
      },
      [MEDIA_BREAKPOINTS.MEDIUM]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.MEDIUM] * 0.25}px`,
      },
      [MEDIA_BREAKPOINTS.SMALL]: {
        marginTop: `${vrLineHeight[MEDIA_BREAKPOINTS.SMALL] * 0.25}px`,
      },
    },
    quoteTop: {
      display: 'flex',
      flexDirection: 'row',
      width: '100%',
      alignItems: 'center',
      ':before': {
        content: '""',
        display: 'block',
        height: '1px',
        borderTop: `1px solid ${borderColor}`,
        flex: '2 0 0',
      },
      ':after': {
        content: '""',
        display: 'block',
        height: '1px',
        borderTop: `1px solid ${borderColor}`,
        flex: '2 0 0',
      },
    },
  });
};

export const QuoteComponent: React.FunctionComponent<QuoteComponentProps> = props => {
  const { text, cite, iconColor, vrLineHeight, additionalStyle, dataId, fontSizesStyles } = props;
  const styles = getStyles(props);
  const citeStyle = StyleSheet.create({
    style: fontSizesStyles[TYPOGRAPHY_SIZE.NORMAL],
  });

  return (
    <blockquote data-id={dataId} className={css(styles.wrapper, additionalStyle.style)}>
      <div className={css(styles.quoteTop)}>
        <QuoteIcon iconColor={iconColor} vrLineHeight={vrLineHeight} />
      </div>
      <H3 styles={semanticCSS(styles.text)}>{text}</H3>
      {
        cite ? (
          <cite className={css(styles.cite, citeStyle.style)}>
            -&nbsp;
            {cite}
          </cite>
        ) : null
      }

    </blockquote>
  );
};

const mapThemeProps = (theme: QuoteComponentThemeProps) => {
  return {
    vrLineHeight: theme.vrLineHeight,
    borderColor: theme.borderColor,
    textColor: theme.textColor,
    citeColor: theme.citeColor,
    iconColor: theme.iconColor,
    fontSizesStyles: theme.fontSizesStyles,
  };
};

export const Quote = (props: QuoteComponentDataProps & MandatoryAdditionalStyle) => {
  const themeContext = useTheme(mapThemeProps, componentConfigurationKey);
  const { additionalStyle } = props;
  return <ErrorBoundary><QuoteComponent {...props} {...themeContext} additionalStyle={additionalStyle} /></ErrorBoundary>;
};
